import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";

const GRAD = {
  backgroundImage: "linear-gradient(90deg, #00C2D1 0%, #2EE59D 100%)",
};

function Principle({ title, children }) {
  return (
    <div className="border-t hairline pt-6">
      <h3 className="font-display text-[#1A3A61] text-2xl md:text-[26px] leading-[1.15] tracking-[-0.015em] font-semibold">
        {title}
      </h3>
      <div className="mt-3 text-[#1A3A61]/75 text-[16px] leading-[1.7] space-y-3">
        {children}
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <>
      <SEO
        title="Manifesto — Nossa visão sobre o futuro das licitações | licito.guru"
        description="O propósito da licito.guru é vencer a insegurança jurídica e a burocracia nas licitações e nos contratos públicos. Conheça o manifesto, os princípios e os fundadores da plataforma."
        path="/manifesto"
      />
    <PageShell
      kicker="Manifesto"
      title={
        <>
          Nascemos para{" "}
          <span className="bg-clip-text text-transparent" style={GRAD}>
            vencer a insegurança jurídica e a burocracia
          </span>{" "}
          em licitações e contratos.
        </>
      }
    >
      <article className="mt-6 md:mt-10 max-w-3xl mx-auto">
        <div className="space-y-12">
          {/* Intro block */}
          <section className="space-y-5 text-[#1A3A61]/80 text-[17px] md:text-[18px] leading-[1.7]">
            <p>
              A Administração Pública não pode continuar perdendo tempo com processos
              lentos, informações dispersas e ferramentas que não compreendem a
              realidade das contratações públicas.
            </p>
            <p>
              <span className="text-[#1A3A61] font-medium">
                A licito.guru nasceu para mudar essa realidade.
              </span>
            </p>
            <p>
              Existimos para transformar insegurança em fundamentação, burocracia em
              fluidez e trabalho repetitivo em decisões mais rápidas, técnicas e
              seguras.
            </p>
            <p className="font-display text-[#1A3A61] text-xl md:text-2xl leading-snug tracking-[-0.01em]">
              Não somos apenas mais uma inteligência artificial.
            </p>
            <p>
              Somos uma inteligência artificial especializada em licitações e
              contratos públicos, desenvolvida para apoiar quem precisa
              pesquisar, elaborar, analisar, justificar e decidir todos os dias.
            </p>
          </section>

          {/* Por que existimos */}
          <section>
            <h2 className="font-display text-[#1A3A61] text-3xl md:text-4xl leading-tight tracking-[-0.02em]">
              Por que existimos
            </h2>
            <div className="mt-6 space-y-5 text-[#1A3A61]/80 text-[16px] md:text-[17px] leading-[1.7]">
              <p>
                As contratações públicas movimentam uma parcela relevante dos
                recursos do país e impactam diretamente a qualidade dos serviços
                entregues à população.
              </p>
              <p>
                Mesmo assim, servidores e gestores ainda enfrentam normas complexas,
                entendimentos divergentes, excesso de documentos, fontes dispersas e
                o receio constante de tomar uma decisão sem fundamentação suficiente.
              </p>
              <p>
                <span className="text-[#1A3A61] font-medium">
                  Acreditamos que o servidor público não precisa de mais uma
                  ferramenta genérica.
                </span>
              </p>
              <p>
                Precisa de uma inteligência que compreenda a Lei nº 14.133/2021, a
                jurisprudência, as orientações dos órgãos de controle e a prática
                real de quem conduz processos de contratação.
              </p>
              <p>
                Uma inteligência que não apenas responda perguntas, mas ajude a
                construir o caminho.
              </p>
              <p>
                Que encontre informações, organize evidências, produza documentos,
                identifique riscos e apresente elementos para que cada decisão seja
                tomada com mais confiança.
              </p>
            </div>
          </section>

          {/* Quem somos */}
          <section>
            <h2 className="font-display text-[#1A3A61] text-3xl md:text-4xl leading-tight tracking-[-0.02em]">
              Quem somos
            </h2>
            <div className="mt-6 space-y-5 text-[#1A3A61]/80 text-[16px] md:text-[17px] leading-[1.7]">
              <p>
                A licito.guru nasceu do encontro entre conhecimento jurídico,
                experiência em gestão pública e tecnologia.
              </p>
              <p>
                A plataforma foi idealizada e é liderada por profissionais que
                conhecem profundamente os desafios das contratações públicas:
              </p>
              <ul className="space-y-4 pl-1">
                <li>
                  <span className="text-[#1A3A61] font-medium">
                    Joel de Menezes Niebuhr
                  </span>
                  <span className="block mt-1">
                    Doutor em Direito do Estado pela PUC/SP. Mestre em Direito
                    pela UFSC. Professor em cursos de capacitação em licitações
                    públicas há mais de 20 anos. Autor das obras que fundamentam
                    o banco de dados jurídicos da licito.guru IA.
                  </span>
                </li>
                <li>
                  <span className="text-[#1A3A61] font-medium">
                    Gustavo Schiefler
                  </span>
                  <span className="block mt-1">
                    Doutor em Direito do Estado (USP). Mestre e graduado em
                    Direito (UFSC). Foi pesquisador visitante no
                    Max-Planck-Institut, em Hamburgo. Educação Executiva pela
                    Harvard Law School (Program on Negotiation). Professor em
                    cursos de capacitação em licitações públicas há mais de 10
                    anos. Professor da pós-graduação (mestrado e especialização)
                    do IDP em Brasília.
                  </span>
                </li>
                <li>
                  <span className="text-[#1A3A61] font-medium">
                    Luís Paulo Severo
                  </span>
                  <span className="block mt-1">
                    CEO do CEAP Brasil, uma das maiores escolas de gestão
                    pública do país, com mais de 15 anos de atuação e mais de
                    30 mil gestores públicos capacitados.
                  </span>
                </li>
              </ul>
              <p>
                Dessa união nasceu uma plataforma feita para a realidade da
                Administração Pública, para quem vive o dia a dia das
                licitações, dos pequenos municípios às grandes estruturas
                públicas.
              </p>
            </div>
          </section>

          {/* No que acreditamos */}
          <section>
            <h2 className="font-display text-[#1A3A61] text-3xl md:text-4xl leading-tight tracking-[-0.02em] mb-8">
              No que acreditamos
            </h2>
            <div className="space-y-10">
              <Principle title="Especialização acima do generalismo">
                <p>
                  Contratações públicas exigem contexto, conhecimento jurídico e
                  compreensão da rotina administrativa.
                </p>
                <p>
                  Por isso, não adaptamos uma inteligência genérica ao setor
                  público. Desenvolvemos uma inteligência especializada para ele.
                </p>
              </Principle>

              <Principle title="Evidências antes de respostas">
                <p>Uma decisão segura precisa ser verificável.</p>
                <p>
                  Por isso, nossas entregas apresentam as fontes, os dados e os
                  fundamentos utilizados, permitindo que o servidor revise, valide e
                  decida com autonomia.
                </p>
              </Principle>

              <Principle title="Tecnologia que instrui, não substitui">
                <p>
                  A inteligência artificial não substitui o conhecimento, a
                  responsabilidade ou o julgamento do servidor.
                </p>
                <p>
                  Ela elimina tarefas repetitivas, organiza informações e amplia a
                  capacidade de análise para que as pessoas possam se dedicar ao que
                  realmente exige experiência e decisão.
                </p>
              </Principle>

              <Principle title="Cada órgão possui sua própria realidade">
                <p>Nenhum órgão público funciona exatamente como outro.</p>
                <p>
                  No plano personalizado, documentos, entendimentos jurídicos,
                  modelos, contratos, normas internas e bases institucionais passam
                  a integrar o ambiente da licito.guru.
                </p>
                <p>
                  A tecnologia se adapta ao órgão, e não o órgão à tecnologia.
                </p>
              </Principle>

              <Principle title="Menos burocracia. Mais capacidade de agir.">
                <p>
                  Processos que antes levavam horas ou dias podem ser
                  estruturados em minutos, sempre com revisão e validação
                  humana.
                </p>
                <p>Não para acelerar decisões de qualquer maneira.</p>
                <p>
                  Mas para permitir que elas sejam tomadas com mais informação, mais
                  clareza e mais segurança.
                </p>
              </Principle>

              <Principle title="O futuro das contratações públicas começa agora">
                <p>
                  Acreditamos em uma Administração Pública mais inteligente,
                  eficiente e preparada.
                </p>
                <p>
                  Uma Administração que utiliza tecnologia com responsabilidade,
                  valoriza seus servidores e transforma conhecimento em
                  contratações públicas mais eficientes e seguras.
                </p>
                <p className="text-[#1A3A61] font-medium">É para isso que existimos.</p>
                <p>
                  Para colocar a inteligência artificial a serviço de quem trabalha
                  pelo interesse público.
                </p>
              </Principle>
            </div>
          </section>

          {/* Signature */}
          <section className="border-t hairline pt-10">
            <p className="font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.02em]">
              licito.guru
            </p>
            <p className="mt-3 font-display text-[#1A3A61]/80 text-lg md:text-xl leading-snug tracking-[-0.01em]">
              Inteligência artificial especializada para quem decide o futuro das contratações
              públicas.
            </p>
          </section>
        </div>
      </article>
    </PageShell>
    </>
  );
}
